import { Group, Stack, TextInput } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import type { Outcome } from '../../../../api/strategy-client/strategy-client.type';
import { useTranslation } from 'react-i18next';
import { EmptyFilterResult } from '../../../../components/filters/empty-filter-result';
import { baseWhite, inkLight } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Table } from '../../../data-management/components/table';
import { areDataArraysDifferent, sortRiskAnalysisData } from '../risk-analysis.helpers';
import { getOutcomeTableColumns, handleDownloadOutcomeTableCSV } from './risk-analysis-outcomes-table.hooks';

const ITEMS_PER_PAGE = 20;

type Props = {
  outcomes: Outcome[];
  onOutcomeClick?: (outcome: Outcome) => void;
};

export function RiskAnalysisOutcomesTable({ outcomes, onOutcomeClick }: Props) {
  const i18n = useTranslation();
  const [selectedPage, setSelectedPage] = useState(1);
  const [displayedOutcomes, setDisplayedOutcomes] = useState(outcomes);
  const [searchTerm, setSearchTerm] = useState('');
  const prevOutcomesRef = useRef<Outcome[]>(outcomes);
  const prevSearchTermRef = useRef<string>(searchTerm);

  // Update displayed outcomes when outcomes content or search term changes
  useEffect(() => {
    const outcomesChanged = areDataArraysDifferent(prevOutcomesRef.current, outcomes);
    const searchTermChanged = prevSearchTermRef.current !== searchTerm;

    if (outcomesChanged || searchTermChanged) {
      const filtered = outcomes.filter((outcome) => {
        const matchesSearch =
          outcome.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (outcome.key && outcome.key.toLowerCase().includes(searchTerm.toLowerCase()));

        return matchesSearch;
      });

      setDisplayedOutcomes(filtered);

      // Only reset the page if outcomes content actually changed, not on search
      if (outcomesChanged) {
        setSelectedPage(1);
      }

      // Update refs to current values
      prevOutcomesRef.current = outcomes;
      prevSearchTermRef.current = searchTerm;
    }
  }, [outcomes, searchTerm]);

  const sortData = (data: Outcome[], sortBy: keyof Outcome, direction: 'asc' | 'desc'): Outcome[] => {
    return sortRiskAnalysisData(data, sortBy, direction);
  };

  return (
    <Stack>
      <Group justify="space-between">
        <Button
          variant="secondary"
          radius="xl"
          size="sm"
          leftSection={<Icon name="format_list_bulleted" color={baseWhite} variant="filled" size={16} />}
        >
          Table View
        </Button>
        <Group>
          <TextInput
            placeholder={`Search ${i18n.t('common.outcomes').toLowerCase()}...`}
            radius="xl"
            size="sm"
            value={searchTerm}
            leftSection={<Icon name="search" variant="filled" size={16} color={inkLight} />}
            style={{ width: 300 }}
            onChange={(event) => {
              setSearchTerm(event.currentTarget.value);
            }}
          />
          <Button
            variant="secondary"
            radius="xl"
            size="sm"
            leftSection={<Icon name="download" color={baseWhite} variant="filled" size={16} />}
            onClick={() => handleDownloadOutcomeTableCSV(outcomes, i18n.t)}
          >
            Export
          </Button>
        </Group>
      </Group>
      {displayedOutcomes.length > 0 ? (
        <Table<Outcome>
          data={displayedOutcomes}
          columns={getOutcomeTableColumns(i18n.t)}
          getRowId={(outcome: Outcome) => outcome.id}
          stickyHeader
          onRowClick={(outcome: Outcome) => onOutcomeClick?.(outcome)}
          pageSize={ITEMS_PER_PAGE}
          currentPage={selectedPage}
          totalItems={displayedOutcomes.length}
          onPageChange={(page: number) => {
            setSelectedPage(page);
          }}
          customSort={sortData}
        />
      ) : (
        <EmptyFilterResult objectName={i18n.t('common.outcomes').toLowerCase()} />
      )}
    </Stack>
  );
}
